import EventEmitter from 'eventemitter3';
import WsConnection from './WsConnection';
import { proto } from '../proto/ListenerProto';
import { wsConnectionClosingCodes } from '../constants/backend';
import { Protobuf } from '../errors/Errors';
import { captureException } from '../tracker/raven';
import { mainConnLogger as logger } from '../utils/logger/loggerFactory';
const mainLog = logger;

const encodeRequest = (payload: unknown) => {
    const description = proto.Request.verify(payload);
    if (description) {
        const error = new Protobuf.ListenerRequestError({ description, payload });
        captureException(error);
        return mainLog.error(error.message);
    }
    const request = proto.Request.create(payload);
    return proto.Request.encode(request).finish();
};

export default class MainConnection extends EventEmitter {
    private _ws: WsConnection | null;
    private _params: { protocol?: string; host: string; port?: number; type?: string };

    constructor(params: { protocol?: string; host: string; port?: number; type?: string }) {
        super();
        this._ws = null;
        this._params = params;
    }

    start() {
        const meta = {
            description: 'Main listener connection'
        };
        this._ws = new WsConnection({ ...this._params, encodeRequest, meta });
        this._ws.on('opened', () => this.emit('opened'));
        this._ws.on('closed', (event: unknown) => this.emit('closed', event));
        this._ws.on('message', (data: unknown) => this.emit('message', data));
    }

    sendAuthorizationRequest(secret: string) {
        const request = { authorizationRequest: { payload: secret } };
        this._ws?.send(request);
        mainLog.log('authorization request', request);
    }

    sendRefreshTokenRequest(token: string) {
        const request = { jwt: { token } };
        this._ws?.send(request);
        mainLog.log('refresh token request', request);
    }

    sendDeviceListRequest() {
        const request = { deviceListRequest: {} };
        this._ws?.send(request);
        mainLog.log('device list request', request);
    }

    sendStartMixedRecordingRequest(audioDevices: unknown, encoding = proto.AudioEncoding.FLAC) {
        // eslint-disable-next-line no-param-reassign
        if ((window as Window & { hasOpus?: boolean }).hasOpus) encoding = proto.AudioEncoding.OPUS;
        const request = { startMixedRecordingRequest: { audioDevices, encoding } };
        this._ws?.send(request);
        mainLog.log('start mixed recording request', request);
    }

    sendStopMixedRecordingRequest() {
        const request = { stopMixedRecordingRequest: {} };
        this._ws?.send(request);
        mainLog.log('stop mixed recording request', request);
    }

    sendServiceVersionRequest() {
        const request = { serviceVersionRequest: {} };
        this._ws?.send(request);
        mainLog.log('service version request', request);
    }

    sendEventBroadcasterRequest() {
        const request = { eventBroadcasterRequest: {} };
        this._ws?.send(request);
        mainLog.log('event broadcaster request', request);
    }

    sendSystemInformationRequest() {
        const request = { systemInformationRequest: {} };
        this._ws?.send(request);
        mainLog.log('system information request', request);
    }

    finishConnection(reason: string | null = null) {
        mainLog.log('finish main listener connection');
        this.sendStopMixedRecordingRequest();
        // Native WebSocket.close forces any provided first arg through `unsigned short`
        // coercion, so passing `undefined` becomes `0` — which the spec rejects.
        // Pass the explicit DEFAULT code whenever we want a reason, or call close() bare.
        if (reason != null) {
            this._ws?.close(wsConnectionClosingCodes.DEFAULT, reason);
        } else {
            this._ws?.close();
        }
    }
}
